import { useEffect, useState } from "react";
import {
    List,
    RecordContextProvider,
    ShowButton,
    useListContext,
} from "react-admin";
import { MenuItem } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";

import { PaymentStatusEnum } from "../../enums/PaymentStatusEnum";
import { ComboResponse } from "../../interfaces/Combo/ComboResponse";
import { useAppSelector } from "../../store/hooks";
import ResourceSelectDialogButton from "../Common/ResourceSelectDialogButton";
import { formatDateTime } from "../../utils/DateTimeUtils";

//Common styles
import {
    FilterContainer,
    FilterInput,
    FilterSection,
    FilterTextField,
    FilterWrapper,
    FIlterHeader,
    SectionHeader,
    SectionTitle,
    WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { FilterIcon } from "../Svg";
import TransactionButton from "../Common/TransactionButton";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

const PRODUCT_TYPE_OPTIONS = ["TRAIL", "ONE_MEAL_SERVICE"];

const ComboFilters = () => {
    const { filterValues, setFilters, isLoading } = useListContext();

    const [phoneInput, setPhoneInput] = useState<string>(
        filterValues.customerPhoneNumber ?? ""
    );

    // Keep the phone input in sync when filters are cleared elsewhere
    useEffect(() => {
        setPhoneInput(filterValues.customerPhoneNumber ?? "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterValues.customerPhoneNumber]);

    const applyFilter = (key: string, value: string | "") => {
        const next: any = { ...filterValues };
        if (value === "" || value === null || value === undefined) {
            delete next[key];
        } else {
            next[key] = value;
        }
        setFilters(next, {}, false);
    };

    // Debounce the phone number text input
    useEffect(() => {
        const timer = setTimeout(() => {
            if ((filterValues.customerPhoneNumber ?? "") !== phoneInput) {
                applyFilter("customerPhoneNumber", phoneInput);
            }
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneInput]);

    return (
        <FilterWrapper>
            <FIlterHeader>
                <FilterSection style={{ marginBottom: "0" }}>
                    <SectionHeader>
                        <WorkSpaceTitleRow>
                            <FilterIcon color="#1976d2" height={20} width={20} />
                            <SectionTitle>Filters</SectionTitle>
                        </WorkSpaceTitleRow>
                    </SectionHeader>
                    <TransactionButton
                        name="Clear Filter"
                        onClickHandler={() => setFilters({}, {}, false)}
                    />
                </FilterSection>
            </FIlterHeader>

            <FilterContainer>
                <FilterInput
                    value={phoneInput}
                    size="small"
                    variant="outlined"
                    onChange={(e) => setPhoneInput(e.target.value)}
                    label="Customer Phone"
                    autoComplete="off"
                />

                <DatePicker
                    label="Expire At"
                    disableMaskedInput
                    value={
                        filterValues.expireAt
                            ? moment(filterValues.expireAt, "YYYY-MM-DD").toDate()
                            : null
                    }
                    inputFormat="dd/MM/yyyy"
                    disabled={isLoading}
                    onChange={(value) =>
                        applyFilter(
                            "expireAt",
                            value ? moment(value).format("YYYY-MM-DD") : ""
                        )
                    }
                    renderInput={(params) => (
                        <FilterTextField {...params} size="small" variant="outlined" />
                    )}
                />

                <FilterTextField
                    select
                    label="Product Type"
                    size="small"
                    variant="outlined"
                    value={filterValues.productType ?? ""}
                    disabled={isLoading}
                    onChange={(e) => applyFilter("productType", e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">All</MenuItem>
                    {PRODUCT_TYPE_OPTIONS.map((type) => (
                        <MenuItem key={type} value={type}>
                            {type}
                        </MenuItem>
                    ))}
                </FilterTextField>

                <FilterTextField
                    select
                    label="Payment Status"
                    size="small"
                    variant="outlined"
                    value={filterValues.paymentStatus ?? ""}
                    disabled={isLoading}
                    onChange={(e) => applyFilter("paymentStatus", e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">All</MenuItem>
                    {Object.values(PaymentStatusEnum).map((status) => (
                        <MenuItem key={status} value={status}>
                            {status}
                        </MenuItem>
                    ))}
                </FilterTextField>
            </FilterContainer>
        </FilterWrapper>
    );
};

const lastOrderDetail = (row: ComboResponse) =>
    row?.orderDetailsData?.[row?.orderDetailsData?.length - 1];

const ComboTable = () => {
    const isCustomSelectOpen = useAppSelector(
        (state) => state.customSelect.value.open
    );
    const { data, isLoading, page, perPage, total, setPage, setPerPage, sort, setSort } =
        useListContext();

    const columns: CommonListColumn<ComboResponse>[] = [
        {
            label: "Actions",
            stopRowClick: true,
            render: (row) => (
                <RecordContextProvider value={row}>
                    {isCustomSelectOpen ? <ResourceSelectDialogButton /> : <ShowButton />}
                </RecordContextProvider>
            ),
        },
        { label: "Id", field: "id", sortable: true },
        {
            label: "Customer Name",
            render: (row) =>
                [row?.customerData?.fname, row?.customerData?.lname]
                    .filter((each) => each !== null && each !== undefined)
                    .join(" ") || "-",
        },
        { label: "Customer Mobile", field: "customerData.mobile" },
        {
            label: "Purchased Pack Name",
            render: (row) => lastOrderDetail(row)?.discountCouponCode ?? "-",
        },
        {
            label: "Coupons Used",
            render: (row) =>
                lastOrderDetail(row)?.discountCouponData?.totalTimesUsed ?? "-",
        },
        {
            label: "Max Usage Limit",
            render: (row) =>
                lastOrderDetail(row)?.discountCouponData?.maximumUsageLimit ?? "-",
        },
        {
            label: "Coupon Expiry date",
            nowrap: true,
            render: (row) => {
                const expiresAt = lastOrderDetail(row)?.discountCouponData?.expiresAt;
                return expiresAt ? formatDateTime(expiresAt) : "-";
            },
        },
        { label: "Payment Status", field: "paymentStatus", type: "status" },
        { label: "Created By Type", field: "createdByType" },
        { label: "Created At", field: "createdAt", type: "datetime", minWidth: 175 },
        { label: "Updated At", field: "updatedAt", type: "datetime", minWidth: 175 },
    ];

    return (
        <CommonListTable
            title="Pack"
            columns={columns}
            data={data ?? []}
            loading={isLoading}
            page={page - 1}
            rowsPerPage={perPage}
            total={total ?? 0}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={(rows) => {
                setPerPage(rows);
                setPage(1);
            }}
            maxTableHeight="calc(100vh - 320px)"
            sort={sort ? { field: sort.field, order: sort.order as "ASC" | "DESC" } : undefined}
            onSortChange={(field, order) => setSort({ field, order })}
        />
    );
};

const ComboList = (props: any) => {
    return (
        <List
            {...props}
            sort={{ field: "id", order: "DESC" }}
            pagination={false}
            sx={{
                "& .RaList-content": {
                    boxShadow: "none",
                    backgroundColor: "transparent",
                },
            }}
        >
            <ComboFilters />

            <br />

            <ComboTable />
        </List>
    );
};

export default ComboList;
